import { useEffect, useMemo, useState } from 'react';

import {
    getNextApiRoute,
    typedFetch,
    usePageLayout,
    usePrevious,
} from '@libs/common-next';

import type { Nullable } from '@libs/core';
import * as Sentry from '@sentry/nextjs';
import { useProductDetails } from './useProductDetails';
import { getAccessToken } from '@libs/api-proxy-client/utils';

type TreatPointsResult = { treatPoints?: string };

export function useDeliveryTreatPoints(qty: number, shopId?: number) {
    const { flags } = usePageLayout();
    const {
        selectedProduct: { id, price: { number: price } = { number: 0 } },
    } = useProductDetails();
    const accessToken = getAccessToken();
    const [treatPoints, setTreatPoints] = useState<Nullable<string>>(null);
    const [isLoading, setIsLoading] = useState(true);

    const params = useMemo(() => {
        const queryParams = {
            qty: qty.toString(),
            sku: id.toString(),
            price: price.toString(),
            shopId: shopId?.toString() ?? '',
        };

        return new URLSearchParams(queryParams).toString();
    }, [id, price, qty, shopId]);

    useEffect(() => {
        async function fetchPoints() {
            const url = getNextApiRoute(`loyalty/points/estimate?${params}`);

            const result = await typedFetch<TreatPointsResult>(url, {
                withLogs: false,
            });

            if (!result) {
                Sentry.captureException(
                    'Loyalty Points API Error: Feature Flag Is Enabled - No Response Was Obtained'
                );
            }
            setTreatPoints(result?.treatPoints);
            setIsLoading(false);
        }

        // zak - move this out of FE and into fetchPoints call
        async function estimateGuestPoints() {
            const pointsPerDollar =
                Number(
                    process.env.NEXT_PUBLIC_LOYALTY_EARNED_POINTS_PER_DOLLAR
                ) || 10.0;
            const totalPrice = price * qty;
            const estimatedPoints = Math.trunc(
                totalPrice * pointsPerDollar
            ).toString();
            setTreatPoints(estimatedPoints);
            setIsLoading(false);
        }

        if (accessToken && flags?.loyaltyPointsApiEnabled) {
            fetchPoints();
        } else {
            estimateGuestPoints();
        }
    }, [accessToken, params, price, qty, flags?.loyaltyPointsApiEnabled]);

    const prevTreatPoints = usePrevious(treatPoints);

    return {
        treatPoints: treatPoints ?? prevTreatPoints,
        isLoading,
    };
}
